import { Row, Col, Alert, Form, Card, Button, Nav, Badge } from "react-bootstrap";
import useSWR from "swr";
import AuthenticatedWrapper from "../../components/wrapper";
import {
  fetcher,
  formIconStyle,
  getParameterByName,
  serverUrl,
  swrConfig,
} from "../../helpers/utility";
import NavigationBack from "../../components/NavigationBack";
import Loading from "../../components/loading";
import InputReadOnly from "../../components/inputReadOnly";
import { GENDER, NEEDS } from "../../helpers/constants";
import { format } from "date-fns";
import variables from "../../styles/utils.module.scss";
import style from "../../styles/enquiry.module.scss";
import SuitabilitySummary from "../../components/SuitabilitySummary";
import IconButton from "../../components/iconButton";
import CloseEnquiryModal from './Search/closeEnquiryModal';
import CompleteEnquiryModal from './Search/completeEnquiryModal';
import { useCallback, useState } from 'react';
import ConfirmEnquiryModal from './Search/confirmEnquiryModal';


function Enquiry({ user }) {
  const enquiryId = getParameterByName("enquiryId");
  const [activeTab, setActiveTab] = useState("details");
  const [viewConfirmEnquiryModal, setViewConfirmEnquiryModal] = useState(false);
  const [viewCloseEnquiryModal, setViewCloseEnquiryModal] = useState(false);
  const [viewCompleteEnquiryModal, setViewCompleteEnquiryModal] = useState(false);

  function closeConfirmEnquiryModal() {
    setViewConfirmEnquiryModal(false);
  }

  function closeCloseEnquiryModal() {
    setViewCloseEnquiryModal(false);
  }

  function closeCompleteEnquiryModal() {
    setViewCompleteEnquiryModal(false);
  }

  function openConfirmEnquiryModal() {
    setViewConfirmEnquiryModal(true);
  }

  // Need to cross reference the location info with the placement info, if applicable
  const {
    data: enquiryData,
    error: enquiryError,
    isLoading: enquiryLoading,
  } = useSWR(enquiryId ? `${serverUrl}/enquiry/id/${enquiryId}` : null, fetcher, swrConfig);

  function getOverallNeedsVariant(needs) {
    if (needs === NEEDS.LOW) {
      return `success`;
    }
    if (needs === NEEDS.MEDIUM) {
      return `warning`;
    }
    if (needs === NEEDS.HIGH) {
      return `danger`;
    }
    return ``;
  }

  function getEnquiryStatus(enquiryData) {
    const enquiry = enquiryData?.enquiry;
    const placement = enquiryData?.placement;
    
    // 1. Enquiry successfully completed and placement was filled via this enquiry
    if (placement?.hasBeenPlaced === true && enquiry?.completed === true) {
      return {
        label: `Completed`,
        text: `Completed Placement on ${format(placement?.placementDate, "dd/MM/yyyy")}`,
        variant: "success",
      };
    }

    // 2. Enquiry successfully completed and placement was filled via this enquiry
    if (enquiry?.suitable === true) {
      return {
        label: `Suitable`,
        text: `Marked as suitable by ${enquiry.suitableBy} on ${format(enquiry.suitableAt, "dd/MM/yyyy")}`,
        variant: "success",
      };
    }
    
    // 3. Enquiry completed but placement not filled (edge case - enquiry marked complete but placement not placed)
    if (enquiry?.completed === true) {
      return {
        label: `Completed`,
        text: `Completed`,
        variant: 'success',
      };
    }
    
    // 4. Enquiry cancelled/closed
    if (enquiry?.cancelled === true) {
      return {
        label: `Closed`,
        text: `Closed by ${enquiry.closedBy} on ${format(enquiry.closedAt, "dd/MM/yyyy")}`,
        variant: 'danger',
      };
    }
    
    // 5. Enquiry is open but placement has been filled via another enquiry
    if (placement?.hasBeenPlaced === true) {
      return {
        label: `Placement Filled`,
        text: `Placement was completed via another enquiry on ${format(placement?.placementDate, "dd/MM/yyyy")}`,
        variant: 'warning',
      };
    }
    
    // 6. Enquiry is open and placement is still open
    return {
      label: `Open`,
      text: `Open`,
      variant: 'primary',
    };
  }

  function getFriendlyOverallNeeds(needs) {
    if (needs === NEEDS.LOW) {
      return `LOW`;
    }
    if (needs === NEEDS.MEDIUM) {
      return `MEDIUM`;
    }
    if (needs === NEEDS.HIGH) {
      return `HIGH`;
    }
    return ``;
  }

   const openCompleteEnquiryModal = useCallback(() => {
     setViewCompleteEnquiryModal(true);
   }, []);

  const openCloseEnquiryModal = useCallback(() => {
    setViewCloseEnquiryModal(true);
  }, []);

  if (enquiryLoading || !enquiryData) {
    return <Loading />;
  }

  if (enquiryError) {
    return (
      <div
        style={{
          display: "flex",
          flex: 1,
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <h1 className="h4">Enquiry not found.</h1>
      </div>
    );
  }

  return (
    <AuthenticatedWrapper user={user}>
      <Row style={{ padding: "5px 0px 60px" }}>
        <NavigationBack />
        <Col xs={12} xl={10} style={{ margin: "0 auto" }}>
          <Row className={`d-flex justify-content-center`}>
            <Col xs={12} lg={8}>
              <Card className="mb-3">
                <Card.Body className="card-body">
                  <h1 className="h2 mb-3">Placement Enquiry</h1>
                  <Nav variant="tabs" style={{ marginBottom: 15 }}>
                    <Nav.Item>
                      <Nav.Link
                        onClick={() => setActiveTab("details")}
                        active={activeTab === "details"}
                      >
                        Details
                      </Nav.Link>
                    </Nav.Item>
                    {enquiryData?.placement?.voiceOfTheChild ? (
                      <Nav.Item>
                        <Nav.Link
                          onClick={() => setActiveTab("voice")}
                          active={activeTab === "voice"}
                        >
                          Voice of the Child
                        </Nav.Link>
                      </Nav.Item>
                    ) : null}
                  </Nav>
                  {activeTab === "details" && (
                    <>
                      {enquiryData?.enquiry?.messages?.length > 0 &&
                      enquiryData?.enquiry?.messages[0]?.message ? (
                        <div style={{ marginTop: 0 }}>
                          <h2 className="h4">Enquiry</h2>
                          <InputReadOnly
                            label={`Message`}
                            type="textarea"
                            value={enquiryData?.enquiry?.messages[0].message}
                          />
                        </div>
                      ) : null}

                      <div style={{ marginTop: 30, breakAfter: "page" }}>
                        <h2 className="h4">Child Details</h2>

                        {!enquiryData?.enquiry.informationSharing &&
                        user?.careProviderOrganisationId ? (
                          <Alert variant="info" style={{ marginTop: 10 }} className="d-print-none">
                            Note: The Local Authority has selected to keep some details private at
                            this stage of the enquiry.
                          </Alert>
                        ) : null}

                        <InputReadOnly
                          label={`Reference`}
                          type="text"
                          value={enquiryData?.placement?.reference}
                        />

                        <InputReadOnly
                          label={`Gender`}
                          type="text"
                          value={GENDER[enquiryData?.placement?.gender]}
                        />
                        <InputReadOnly
                          label={`Date of Birth`}
                          type="text"
                          value={format(enquiryData?.placement?.dob, "dd/MM/yyyy")}
                        />

                        <InputReadOnly
                          label={`Start date`}
                          type="text"
                          value={format(enquiryData?.placement?.dateFrom, "dd/MM/yyyy")}
                        />

                        <InputReadOnly
                          label={`End date`}
                          type="text"
                          value={format(enquiryData?.placement?.dateTo, "dd/MM/yyyy")}
                        />
                      </div>

                      <div style={{ marginTop: 30, breakAfter: "page" }}>
                        <h2 className="h4">Location Details</h2>

                        <InputReadOnly
                          label={`Name`}
                          type="text"
                          value={enquiryData?.location?.name}
                        />

                        <InputReadOnly
                          label={`Address`}
                          type="text"
                          value={`${enquiryData?.location?.address?.addressLine1}, ${enquiryData?.location?.address?.townCity}, ${enquiryData?.location?.address?.postcode} `}
                        />

                        <Form.Group as={Row} className="mb-3">
                          <Col style={{ flexGrow: 1 }}>
                            <InputReadOnly
                              label={`Email`}
                              type="text"
                              value={enquiryData?.location?.email}
                            />
                          </Col>

                          <Form.Label column style={formIconStyle}>
                            <a
                              href={`mailto:${enquiryData?.location?.email}?Subject=RE:%20bPlaced%20Enquiry`}
                              target="_blank"
                            >
                              <i
                                className={`bi bi-envelope`}
                                style={{
                                  fontSize: "16px",
                                  color: variables.green,
                                }}
                              ></i>
                            </a>
                          </Form.Label>
                        </Form.Group>

                        <Form.Group as={Row} className="mb-3">
                          <Col style={{ flexGrow: 1 }}>
                            <InputReadOnly
                              label={`Phone`}
                              type="text"
                              value={enquiryData?.location?.phone}
                            />
                          </Col>

                          <Form.Label column style={formIconStyle}>
                            <a href={`tel:${enquiryData?.location?.phone}`} target="_blank">
                              <i
                                className={`bi bi-telephone`}
                                style={{
                                  fontSize: "16px",
                                  color: variables.green,
                                }}
                              ></i>
                            </a>
                          </Form.Label>
                        </Form.Group>

                        <Form.Group as={Row} className="mb-3">
                          <Col style={{ flexGrow: 1 }}>
                            <InputReadOnly
                              label={`Website`}
                              type="text"
                              value={enquiryData?.location?.website}
                            />
                          </Col>

                          <Form.Label column style={formIconStyle}>
                            <a href={enquiryData?.location?.website} target="_blank">
                              <i
                                className={`bi bi-link-45deg`}
                                style={{
                                  fontSize: "18px",
                                  color: variables.green,
                                }}
                              ></i>
                            </a>
                          </Form.Label>
                        </Form.Group>
                      </div>
                    </>
                  )}

                  {activeTab === "voice" && enquiryData?.placement?.voiceOfTheChild && (
                    <div style={{ marginTop: 0 }}>
                      <h2 className="h4 mb-3">{`In their own words`}</h2>

                      {enquiryData?.placement?.voiceOfTheChild?.preferredName && (
                        <InputReadOnly
                          label={`Would like to be called`}
                          type="text"
                          value={enquiryData?.placement?.voiceOfTheChild?.preferredName}
                        />
                      )}

                      {enquiryData?.placement?.voiceOfTheChild?.childsViewOfThemselves && (
                        <InputReadOnly
                          label={`Child views themselves as`}
                          type="textarea"
                          value={enquiryData?.placement?.voiceOfTheChild?.childsViewOfThemselves}
                          textAreaRows={6}
                        />
                      )}

                      {enquiryData?.placement?.voiceOfTheChild?.whatChildWantsFromPlacement && (
                        <InputReadOnly
                          label={`Child would like to be placed in a home that`}
                          type="textarea"
                          value={
                            enquiryData?.placement?.voiceOfTheChild?.whatChildWantsFromPlacement
                          }
                          textAreaRows={6}
                        />
                      )}

                      {enquiryData?.placement?.voiceOfTheChild?.whatMakesThemHappy && (
                        <InputReadOnly
                          label={`Child feels happiest when`}
                          type="textarea"
                          value={enquiryData?.placement?.voiceOfTheChild?.whatMakesThemHappy}
                          textAreaRows={6}
                        />
                      )}

                      {enquiryData?.placement?.voiceOfTheChild?.whatTheyLike && (
                        <InputReadOnly
                          label={`Child would like you to know that they like`}
                          type="textarea"
                          value={enquiryData?.placement?.voiceOfTheChild?.whatTheyLike}
                          textAreaRows={6}
                        />
                      )}

                      {enquiryData?.placement?.voiceOfTheChild?.whatTheyDislike && (
                        <InputReadOnly
                          label={`Child would like you to know that they don't like`}
                          type="textarea"
                          value={enquiryData?.placement?.voiceOfTheChild?.whatTheyDislike}
                          textAreaRows={6}
                        />
                      )}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} lg={4}>
              <Card style={{ marginTop: 0 }}>
                <Card.Body className="card-body">
                  <div className="d-flex flex-row justify-content-between align-items-center mb-3">
                    <h2 className="h3" style={{ marginBottom: 0 }}>
                      Status
                    </h2>

                    <Badge className="text-uppercase" bg={getEnquiryStatus(enquiryData).variant}>
                      {getEnquiryStatus(enquiryData).label}
                    </Badge>
                  </div>

                  <Alert
                    variant={getEnquiryStatus(enquiryData).variant}
                    className="text-center mb-0"
                  >
                    {getEnquiryStatus(enquiryData).text}
                  </Alert>
                </Card.Body>
              </Card>

              <Card style={{ marginTop: 20 }}>
                <Card.Body className="card-body">
                  {enquiryData?.placement?.overallNeeds ? (
                    <div
                      className="d-flex flex-column justify-content-center align-items-center"
                      style={{ marginTop: 6 }}
                    >
                      <Alert
                        variant={getOverallNeedsVariant(enquiryData?.placement?.overallNeeds)}
                        style={{
                          paddingLeft: 20,
                          paddingRight: 20,
                          width: "100%",
                        }}
                      >
                        <h3 className="h2 text-center">
                          {getFriendlyOverallNeeds(enquiryData?.placement?.overallNeeds)}
                        </h3>
                        <div
                          className="text-center fw-bold"
                          style={{ fontSize: "11px", lineHeight: "14px" }}
                        >
                          LEVEL SUPPORT NEEDS
                        </div>
                      </Alert>
                    </div>
                  ) : null}

                  <div>
                    {enquiryData?.placement?.overallNeeds === NEEDS.LOW
                      ? `At the expected developmental age and ability, requires minimal intervention.`
                      : null}
                    {enquiryData?.placement?.overallNeeds === NEEDS.MEDIUM
                      ? `Require support during the week but this isn't a daily occurrence.`
                      : null}
                    {enquiryData?.placement?.overallNeeds === NEEDS.HIGH
                      ? `Require intensive support daily.`
                      : null}
                  </div>
                </Card.Body>
              </Card>

              <Card style={{ marginTop: 20 }}>
                <Card.Body className="card-body">
                  <h2 className="h3" style={{ marginBottom: 15 }}>
                    Suitability
                  </h2>

                  {enquiryData?.location?.match ? (
                    <SuitabilitySummary match={enquiryData?.location?.match} hideHeader={true} />
                  ) : null}
                </Card.Body>
              </Card>

              <Card style={{ marginTop: 20 }}>
                <Card.Body className="card-body">
                  <h2 className="h3" style={{ marginBottom: 15 }}>
                    Documents
                  </h2>

                  <div className={style.locationFact}>
                    <div className="fw-normal">
                      <i
                        className={`bi bi-file-earmark-text`}
                        style={{
                          fontSize: "14px",
                          color: variables.black,
                          marginRight: 6,
                        }}
                      ></i>
                      No documents found.
                    </div>
                  </div>
                </Card.Body>
              </Card>

              {(user.localAuthorityId &&
                enquiryData?.enquiry?.completed === false &&
                enquiryData?.enquiry?.cancelled === false) ||
              (user.localAuthorityId && enquiryData?.enquiry?.suitable === true && enquiryData?.placement?.hasBeenPlaced === false) ? (
                <Card style={{ marginTop: 20 }} className="d-print-none">
                  <Card.Body className="card-body">
                    <h2 className="h3" style={{ marginBottom: 15 }}>
                      Actions
                    </h2>

                    <p>Update the placement status and sharing preferences.</p>

                    <div className="d-flex flex-column" style={{ marginBottom: 20 }}>
                      {enquiryData?.placement?.hasBeenPlaced === false ? (
                        <Button
                          variant="outline-primary"
                          style={{ marginBottom: 10 }}
                          onClick={() => {
                            openConfirmEnquiryModal();
                          }}
                        >
                          Successful Placement
                        </Button>
                      ) : null}
                      <Button
                        variant="outline-danger"
                        style={{ marginBottom: 0 }}
                        onClick={() => {
                          openCloseEnquiryModal();
                        }}
                      >
                        Close Enquiry
                      </Button>
                    </div>

                    <div className="d-flex flex-column">
                      <div className="fw-bold">Information Sharing</div>
                      <div style={{ marginBottom: 6 }}>
                        Click to share either basic or full details.
                      </div>

                      {enquiryData?.enquiry.informationSharing ? (
                        <Button
                          variant="outline-secondary"
                          style={{ marginBottom: 10 }}
                          onClick={() => {
                            console.log(`TODO`);
                          }}
                        >
                          <i
                            className={`bi bi-exclamation-circle-fill`}
                            style={{
                              fontSize: "12px",
                              color: variables.orange,
                              marginRight: "2px",
                            }}
                          ></i>{" "}
                          Basic Details Shared
                        </Button>
                      ) : (
                        <Button
                          variant="outline-secondary"
                          style={{ marginBottom: 10 }}
                          onClick={() => {
                            console.log(`TODO`);
                          }}
                        >
                          <i
                            className={`bi bi-check-circle-fill`}
                            style={{
                              fontSize: "12px",
                              color: variables.green,
                              marginRight: "2px",
                            }}
                          ></i>{" "}
                          Full Details Shared
                        </Button>
                      )}
                    </div>

                  </Card.Body>
                </Card>
              ) : null}

              {user.careProviderOrganisationId ? (
                <Card style={{ marginTop: 20 }} className="d-print-none">
                  <Card.Body className="card-body">
                    <h2 className="h3" style={{ marginBottom: 15 }}>
                      Actions
                    </h2>

                    <p>
                      Please select a response and we will inform the local authority of your
                      decision.
                    </p>

                    <div className="d-flex flex-column">
                      <Button
                        variant="outline-primary"
                        style={{ marginBottom: 10 }}
                        onClick={() => {
                          openCompleteEnquiryModal();
                        }}
                      >
                        Confirm Suitable
                      </Button>
                      {/* <Button
                        variant="outline-secondary"
                        style={{ marginBottom: 10 }}
                        onClick={() => {
                          console.log(`TODO`);
                        }}
                      >
                        Information Required
                      </Button> */}
                      <Button
                        variant="outline-danger"
                        style={{ marginBottom: 0 }}
                        onClick={() => {
                          // declinePlacement();
                          openCloseEnquiryModal();
                        }}
                      >
                        Decline Enquiry
                      </Button>
                    </div>

                  </Card.Body>
                </Card>
              ) : null}
            </Col>
          </Row>
        </Col>
      </Row>

      <ConfirmEnquiryModal
        enquiry={enquiryData?.enquiry}
        placement={enquiryData?.placement}
        location={enquiryData?.location}
        show={viewConfirmEnquiryModal}
        close={closeConfirmEnquiryModal}
        size={"lg"}
        user={user}
      />

      <CloseEnquiryModal
        enquiry={enquiryData?.enquiry}
        placement={enquiryData?.placement}
        location={enquiryData?.location}
        show={viewCloseEnquiryModal}
        close={closeCloseEnquiryModal}
        size={"lg"}
        user={user}
      />

      <CompleteEnquiryModal
        enquiry={enquiryData?.enquiry}
        placement={enquiryData?.placement}
        location={enquiryData?.location}
        show={viewCompleteEnquiryModal}
        close={closeCompleteEnquiryModal}
        size={"lg"}
        user={user}
      />
    </AuthenticatedWrapper>
  );
}

export default Enquiry;
